import type {
    Category,
    Confidentiality,
    KnowledgeStatus,
    KnowledgeType,
    KnowledgeTypeKey,
    LabelDef,
    TrainingFormat,
    Unit
} from '@/types'

export const KNOWLEDGE_TYPES: KnowledgeType[] = [
    {
        key: 'guide',
        title: 'راهنمای تخصصی',
        description: 'توضیح عمیق یک موضوع فنی یا تخصصی همراه با اصول، الگوها و نمونه‌ها',
        icon: 'book-open',
        template: 'KM - راهنمای تخصصی',
        label: 'km-type-guide'
    },
    {
        key: 'instruction',
        title: 'دستورالعمل',
        description: 'روال گام‌به‌گام و الزام‌آور برای انجام یک کار مشخص',
        icon: 'list-checks',
        template: 'KM - دستورالعمل',
        label: 'km-type-instruction'
    },
    {
        key: 'lesson',
        title: 'تجربه و درس‌آموخته',
        description: 'روایت یک چالش واقعی، راهکار به‌کاررفته و نتیجه آن برای استفاده دیگران',
        icon: 'notebook-pen',
        template: 'KM - درس‌آموخته',
        label: 'km-type-lesson'
    },
    {
        key: 'training',
        title: 'محتوای آموزشی',
        description: 'دوره، ویدئو یا راهنمای آموزشی برای یادگیری یک مهارت یا ابزار',
        icon: 'presentation',
        template: 'KM - محتوای آموزشی',
        label: 'km-type-training'
    },
    {
        key: 'project-doc',
        title: 'مستندات پروژه',
        description: 'مستند مرجع معماری، طراحی یا تصمیمات یک پروژه',
        icon: 'folder-open',
        template: 'KM - مستندات پروژه',
        label: 'km-type-project-doc'
    },
    {
        key: 'research',
        title: 'تحلیل و تحقیق',
        description: 'گزارش تحلیلی، مطالعه تطبیقی یا پیشنهاد مبتنی بر بررسی',
        icon: 'file-search',
        template: 'KM - تحلیل و تحقیق',
        label: 'km-type-research'
    },
    {
        key: 'qa',
        title: 'پرسش و پاسخ',
        description: 'پاسخ مستند به پرسشی که بارها در سازمان تکرار می‌شود',
        icon: 'message-circle-question',
        template: 'KM - پرسش و پاسخ',
        label: 'km-type-qa'
    }
]

export const TYPE_BY_KEY = Object.fromEntries(KNOWLEDGE_TYPES.map(type => [type.key, type])) as Record<KnowledgeTypeKey, KnowledgeType>

export const UNITS: Unit[] = [
    { key: 'architecture', title: 'معماری سامانه‌ها' },
    { key: 'payments', title: 'سامانه‌های پرداخت' },
    { key: 'corporate-banking', title: 'توسعه بانکداری شرکتی' },
    { key: 'digital-banking', title: 'بانکداری دیجیتال' },
    { key: 'card-systems', title: 'سامانه‌های کارت' },
    { key: 'lending', title: 'سامانه‌های تسهیلات' },
    { key: 'infrastructure', title: 'زیرساخت و شبکه' },
    { key: 'security', title: 'امنیت اطلاعات' },
    { key: 'qa', title: 'تضمین کیفیت نرم‌افزار' },
    { key: 'data-analytics', title: 'تحلیل داده و هوش تجاری' },
    { key: 'process-quality', title: 'مدیریت فرایند و کیفیت' },
    { key: 'learning', title: 'آموزش و توسعه سرمایه انسانی' }
]

export const UNIT_BY_KEY = Object.fromEntries(UNITS.map(unit => [unit.key, unit])) as Record<string, Unit>

export const CATEGORIES: Category[] = [
    {
        slug: 'technical',
        title: 'دانش فنی و تخصصی',
        description: 'معماری، زیرساخت، توسعه نرم‌افزار و امنیت سامانه‌های بانکی',
        icon: 'cpu',
        spaceKey: 'TECH',
        owner: 'hamid-norouzi',
        subcategories: [
            { slug: 'software-architecture', title: 'معماری نرم‌افزار', description: 'الگوها، استانداردها و تصمیمات معماری سامانه‌ها' },
            { slug: 'infrastructure', title: 'زیرساخت و شبکه', description: 'سرورها، شبکه، پایش و پایداری سرویس‌ها' },
            { slug: 'software-development', title: 'توسعه نرم‌افزار', description: 'روش‌ها، ابزارها و عیب‌یابی در توسعه سرویس‌ها' },
            { slug: 'information-security', title: 'امنیت اطلاعات', description: 'کنترل‌های امنیتی، ارزیابی آسیب‌پذیری و انطباق' }
        ]
    },
    {
        slug: 'process',
        title: 'دانش فرایندی و سازمانی',
        description: 'فرایندهای کاری، دستورالعمل‌ها و رویه‌های اجرایی سازمان',
        icon: 'workflow',
        spaceKey: 'PROC',
        owner: 'zahra-ghasemi',
        subcategories: [
            { slug: 'workflows', title: 'فرایندهای کاری', description: 'جریان کارها، نقش‌ها و مراجع تأیید' },
            { slug: 'guidelines', title: 'دستورالعمل‌ها', description: 'استانداردها و الزامات مصوب سازمان' },
            { slug: 'procedures', title: 'رویه‌های اجرایی', description: 'روش اجرای گام‌به‌گام فعالیت‌های عملیاتی' }
        ]
    },
    {
        slug: 'project',
        title: 'دانش پروژه‌ای',
        description: 'مستندات، تجربیات و درس‌آموخته‌های پروژه‌های شرکت',
        icon: 'folder-kanban',
        spaceKey: 'PROJ',
        owner: 'reza-ahmadi',
        subcategories: [
            { slug: 'project-docs', title: 'مستندات پروژه‌ها', description: 'معماری، طراحی و تصمیمات کلیدی پروژه‌ها' },
            { slug: 'project-experiences', title: 'تجربیات پروژه', description: 'روایت اجرای پروژه‌ها از نگاه تیم‌ها' },
            { slug: 'lessons-learned', title: 'درس‌آموخته‌ها', description: 'آنچه آموختیم و در پروژه‌های بعدی به کار می‌بریم' }
        ]
    },
    {
        slug: 'learning',
        title: 'آموزش و یادگیری',
        description: 'محتوای آموزشی، راهنماهای کاربردی و آموزش ابزارهای کاری',
        icon: 'graduation-cap',
        spaceKey: 'LEARN',
        owner: 'mohammad-jafari',
        subcategories: [
            { slug: 'training-content', title: 'محتوای آموزشی', description: 'دوره‌ها و ویدئوهای آموزشی' },
            { slug: 'practical-guides', title: 'راهنماهای کاربردی', description: 'راهنمای انجام کارهای روزمره تیم‌ها' },
            { slug: 'tools-training', title: 'آموزش ابزارها', description: 'آشنایی با ابزارهای توسعه، پایش و همکاری' }
        ]
    },
    {
        slug: 'analytics',
        title: 'دانش تحلیلی و مدیریتی',
        description: 'گزارش‌های تحلیلی، مطالعات تخصصی و مستند تصمیمات مدیریتی',
        icon: 'chart-line',
        spaceKey: 'ANLY',
        owner: 'fatemeh-mousavi',
        subcategories: [
            { slug: 'analytical-reports', title: 'گزارش‌های تحلیلی', description: 'تحلیل داده‌های عملیاتی و روندها' },
            { slug: 'specialized-studies', title: 'مطالعات تخصصی', description: 'پژوهش‌ها و مطالعات تطبیقی' },
            { slug: 'management-decisions', title: 'تصمیمات مدیریتی', description: 'مستند تصمیمات کلیدی و دلایل آن‌ها' }
        ]
    },
    {
        slug: 'innovation',
        title: 'نوآوری و بهبود',
        description: 'ایده‌ها، پیشنهادها و راهکارهای بهبود فرایندها و سامانه‌ها',
        icon: 'lightbulb',
        spaceKey: 'INNO',
        owner: 'saeed-rostami',
        subcategories: [
            { slug: 'ideas', title: 'ایده‌ها', description: 'ایده‌های نو برای خدمات و سامانه‌ها' },
            { slug: 'proposals', title: 'پیشنهادها', description: 'پیشنهادهای ساختاریافته برای بررسی' },
            { slug: 'improvement-solutions', title: 'راهکارهای بهبود', description: 'بهبودهای اجراشده و اثر آن‌ها' }
        ]
    }
]

export const CATEGORY_BY_SLUG = Object.fromEntries(CATEGORIES.map(category => [category.slug, category])) as Record<string, Category>

export const subcategoryTitle = (categorySlug: string, subSlug: string): string => {
    return CATEGORY_BY_SLUG[categorySlug]?.subcategories.find(sub => sub.slug === subSlug)?.title ?? ''
}

export const LABELS: LabelDef[] = [
    { key: 'api', title: 'API' },
    { key: 'rest', title: 'REST' },
    { key: 'architecture', title: 'معماری' },
    { key: 'security', title: 'امنیت' },
    { key: 'standard', title: 'استاندارد' },
    { key: 'corporate-banking', title: 'بانکداری شرکتی' },
    { key: 'lessons', title: 'درس‌آموخته' },
    { key: 'requirements', title: 'مدیریت الزامات' },
    { key: 'core-banking', title: 'هسته بانکی' },
    { key: 'payment', title: 'پرداخت' },
    { key: 'shaparak', title: 'شاپرک' },
    { key: 'troubleshooting', title: 'عیب‌یابی' },
    { key: 'network', title: 'شبکه' },
    { key: 'incident', title: 'مدیریت رخداد' },
    { key: 'logging', title: 'لاگ' },
    { key: 'kibana', title: 'Kibana' },
    { key: 'monitoring', title: 'پایش' },
    { key: 'training', title: 'آموزش' },
    { key: 'process', title: 'فرایند' },
    { key: 'documentation', title: 'مستندسازی' },
    { key: 'bpmn', title: 'BPMN' },
    { key: 'deployment', title: 'استقرار' },
    { key: 'checklist', title: 'چک‌لیست' },
    { key: 'devsecops', title: 'DevSecOps' },
    { key: 'onboarding', title: 'همکاران جدید' },
    { key: 'dev-environment', title: 'محیط توسعه' },
    { key: 'access-management', title: 'مدیریت دسترسی' },
    { key: 'microservices', title: 'میکروسرویس' },
    { key: 'kafka', title: 'Kafka' },
    { key: 'saga', title: 'الگوی Saga' },
    { key: 'alerting', title: 'هشداردهی' },
    { key: 'infrastructure', title: 'زیرساخت' },
    { key: 'grafana', title: 'Grafana' },
    { key: 'database', title: 'پایگاه داده' },
    { key: 'migration', title: 'مهاجرت داده' },
    { key: 'card', title: 'کارت' },
    { key: 'lending', title: 'تسهیلات' },
    { key: 'project-docs', title: 'مستندات پروژه' },
    { key: 'mobile-banking', title: 'همراه‌بانک' },
    { key: 'release', title: 'انتشار نسخه' },
    { key: 'feature-flag', title: 'کلید ویژگی' },
    { key: 'performance', title: 'کارایی' },
    { key: 'cache', title: 'کش' },
    { key: 'analytics', title: 'تحلیل داده' },
    { key: 'report', title: 'گزارش' },
    { key: 'ipg', title: 'درگاه پرداخت' },
    { key: 'open-banking', title: 'بانکداری باز' },
    { key: 'fintech', title: 'فین‌تک' },
    { key: 'research', title: 'پژوهش' },
    { key: 'strategy', title: 'راهبرد' },
    { key: 'change-management', title: 'مدیریت تغییرات' },
    { key: 'itil', title: 'ITIL' },
    { key: 'confluence', title: 'Confluence' },
    { key: 'knowledge-management', title: 'مدیریت دانش' },
    { key: 'code-review', title: 'بازبینی کد' },
    { key: 'quality', title: 'کیفیت' },
    { key: 'test-automation', title: 'خودکارسازی آزمون' },
    { key: 'innovation', title: 'نوآوری' },
    { key: 'ai', title: 'هوش مصنوعی' },
    { key: 'support', title: 'پشتیبانی' },
    { key: 'idea', title: 'ایده' },
    { key: 'timeout', title: 'Timeout' },
    { key: 'idempotency', title: 'Idempotency' },
    { key: 'events', title: 'رویدادها' }
]

export const LABEL_BY_KEY = Object.fromEntries(LABELS.map(label => [label.key, label])) as Record<string, LabelDef>

export const labelTitle = (key: string): string => LABEL_BY_KEY[key]?.title ?? key

export const STATUS_META: Record<KnowledgeStatus, { title: string, tone: 'success' | 'warning' | 'danger' | 'neutral' | 'primary', description: string }> = {
    'published': { title: 'منتشرشده', tone: 'success', description: 'بررسی شده و برای همکاران در دسترس است' },
    'in-review': { title: 'در انتظار بررسی', tone: 'warning', description: 'در صف بررسی مالک حوزه قرار دارد' },
    'needs-update': { title: 'نیازمند به‌روزرسانی', tone: 'warning', description: 'مالک دانش در حال بازبینی اطلاعات است' },
    'returned': { title: 'نیازمند اصلاح', tone: 'danger', description: 'بازبین درخواست اصلاح داده است' },
    'draft': { title: 'پیش‌نویس', tone: 'neutral', description: 'هنوز برای بررسی ارسال نشده است' }
}

export const CONFIDENTIALITY_META: Record<Confidentiality, { title: string, description: string }> = {
    public: { title: 'عمومی', description: 'همه کارکنان بهسازان ملت' },
    internal: { title: 'داخلی', description: 'همکاران واحد سازمانی و تیم‌های مرتبط' },
    confidential: { title: 'محرمانه', description: 'فقط افراد و گروه‌های مشخص‌شده' }
}

export const FORMAT_META: Record<TrainingFormat, string> = {
    video: 'ویدئو',
    guide: 'راهنمای گام‌به‌گام',
    slides: 'ارائه',
    workshop: 'کارگاه'
}
